import {
  addYears,
  endOfWeek,
  endOfYear,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { weekdaysNumberMap } from 'shared/constants';
import { Goal as AppGoal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { GoalSortingMode } from 'shared/types/sorting-mode';
import { TimelineTimeframes } from 'shared/types/timeline';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { createOtherLifeArea } from 'shared/utils/create-other-life-area';
import { sortGoals } from 'shared/utils/sort-goals';

import { GoalTimeline } from './goal-timeline';
import { Category, ReorderPositions, SubCategory } from './types';

export type GoalTimeLineContainerProps = {
  timeframe: TimelineTimeframes;
  referenceDate: Timestamp;
  goals: AppGoal[];
  lifeAreas: LifeArea[];
  weekStartsOn: WeekDays;
  title: string;
  onChangeGoal?: (
    id: ID,
    updatedGoal: {
      startDate?: Timestamp;
      deadline?: Timestamp;
      goalSorting?: ID[];
      lifeAreaId?: ID | null;
      parentIds?: ID[] | null;
    },
  ) => void;
  onChangeLifeArea?: (
    lifeAreaId: ID,
    updatedLifeArea: { goalSorting: ID[] },
  ) => void;
  onChangeCollapsedItems?: (ids: ID[]) => void;
  collapsedItems?: ID[];
  onCategoryAdd?: (id: ID) => void;
};

export const GoalTimelineContainer: React.FC<GoalTimeLineContainerProps> = ({
  timeframe,
  referenceDate,
  goals,
  lifeAreas,
  weekStartsOn,
  title,
  onChangeGoal,
  onChangeLifeArea,
  onChangeCollapsedItems,
  collapsedItems = [],
  onCategoryAdd,
}) => {
  const { t } = useTranslation();
  const categories = useMemo(() => {
    // Create a map to store all processed goals for easy lookup
    const processedGoalsMap = new Map<string, SubCategory>();

    // First pass: process all goals without setting up parent-child relationships
    goals.forEach((goal) => {
      processedGoalsMap.set(goal.id, {
        id: goal.id,
        goal,
        subCategories: [],
      });
    });

    // Second pass: establish parent-child relationships using parentIds
    goals.forEach((goal) => {
      if (goal.parentIds?.length) {
        const parentId = goal.parentIds[goal.parentIds.length - 1]; // Use the last parent ID
        const parentCategory = processedGoalsMap.get(parentId);
        const currentCategory = processedGoalsMap.get(goal.id);

        if (parentCategory && currentCategory) {
          parentCategory.subCategories.push(currentCategory);
        }
      }
    });

    // Helper function to recursively sort subcategories
    const sortSubCategories = (
      subCategories: SubCategory[],
      parentGoal: AppGoal,
    ) => {
      return sortGoals(
        subCategories.map((sc) => sc.goal),
        {
          allGoals: goals,
          sortMode: parentGoal.goalSortingMode,
          customOrder: parentGoal.goalSorting,
        },
      ).map(({ id }) => processedGoalsMap.get(id)!);
    };

    // Helper function to recursively process subcategories
    const processSubCategories = (subCategory: SubCategory) => {
      if (subCategory.subCategories.length) {
        subCategory.subCategories = sortSubCategories(
          subCategory.subCategories,
          subCategory.goal,
        );
        // Recursively process each subcategory
        subCategory.subCategories.forEach(processSubCategories);
      }
      return subCategory;
    };

    // Create categories from life areas and their goals
    const result: Category[] = [];

    // Add life areas with goals
    lifeAreas.forEach((lifeArea) => {
      const lifeAreaGoals = goals.filter(
        (goal) => goal.lifeAreaId === lifeArea.id && !goal.parentIds?.length,
      );

      const subCategories: SubCategory[] = lifeAreaGoals.length
        ? // Sort goals within the life area
          sortGoals(lifeAreaGoals, {
            allGoals: goals,
            sortMode: GoalSortingMode.Custom,
            customOrder: lifeArea.goalSorting,
          })
            // Create and process subcategories for each goal
            .map((goal) => {
              const subCategory = processedGoalsMap.get(goal.id);
              if (!subCategory) {
                return undefined;
              }
              return processSubCategories(subCategory);
            })
            .filter((sc): sc is SubCategory => !!sc)
        : [];

      result.push({
        id: lifeArea.id,
        lifeArea,
        subCategories,
      });
    });

    // Add goals without a life area
    const noLifeAreaGoals = goals.filter(
      (goal) => !goal.lifeAreaId && !goal.parentIds?.length,
    );

    if (noLifeAreaGoals.length) {
      // Sort goals without a life area
      const sortedGoals = sortGoals(noLifeAreaGoals, {
        allGoals: goals,
        sortMode: GoalSortingMode.Custom,
      });

      // Create and process subcategories for each goal
      const subCategories = sortedGoals
        .map((goal) => {
          const subCategory = processedGoalsMap.get(goal.id);
          if (!subCategory) {
            return null;
          }
          return processSubCategories(subCategory);
        })
        .filter((sc): sc is SubCategory => sc !== null);

      const otherLifeArea = createOtherLifeArea(t);
      result.push({
        id: otherLifeArea.id,
        lifeArea: otherLifeArea,
        subCategories,
      });
    }

    return result;
  }, [goals, lifeAreas, t]);

  const timelineRef = useRef<HTMLDivElement>(null);

  const dateRange = useMemo(
    () => ({
      startDate:
        timeframe === TimelineTimeframes.Month
          ? startOfWeek(startOfYear(addYears(referenceDate, -2)), {
              weekStartsOn: weekdaysNumberMap[weekStartsOn],
            })
          : startOfYear(addYears(referenceDate, -2)),
      endDate:
        timeframe === TimelineTimeframes.Month
          ? endOfWeek(endOfYear(addYears(referenceDate, 10)), {
              weekStartsOn: weekdaysNumberMap[weekStartsOn],
            })
          : endOfYear(addYears(referenceDate, 10)),
    }),
    [referenceDate, timeframe, weekStartsOn],
  );

  const _toggleCollapse = (id: ID) =>
    onChangeCollapsedItems?.(
      collapsedItems?.includes(id)
        ? collapsedItems?.filter((itemId) => itemId !== id)
        : [...collapsedItems, id],
    );

  const handleUpdateDates = (
    goalId: string,
    newStartDate: Timestamp,
    newEndDate: Timestamp,
  ) => {
    // Call the onChangeGoal callback if provided
    if (onChangeGoal) {
      onChangeGoal(goalId as ID, {
        startDate: newStartDate,
        deadline: newEndDate,
      });
    }
  };

  const handleReorderGoals = (
    draggedId: string,
    targetId: string,
    position: ReorderPositions,
  ) => {
    if (!targetId || draggedId === targetId) {
      return;
    }
    const newData = structuredClone(categories);
    let draggedItem: SubCategory | null = null;

    const findAndRemove = (subCategories: SubCategory[]): boolean => {
      for (let i = 0; i < subCategories.length; i++) {
        if (subCategories[i].goal.id === draggedId) {
          const [_draggedItem] = subCategories.splice(i, 1);
          draggedItem = _draggedItem;
          return true;
        }
        if (subCategories[i].subCategories.length > 0) {
          if (findAndRemove(subCategories[i].subCategories)) {
            return true;
          }
        }
      }
      return false;
    };

    newData.forEach((c) => findAndRemove(c.subCategories));

    const findAndInsert = (subCategories: SubCategory[]): boolean => {
      for (let i = 0; i < subCategories.length; i++) {
        if (subCategories[i].goal.id === targetId) {
          if (position === ReorderPositions.Inside) {
            subCategories[i].subCategories.unshift(draggedItem!);
          } else {
            const insertIndex =
              position === ReorderPositions.Before ? i : i + 1;
            subCategories.splice(insertIndex, 0, draggedItem!);
          }
          return true;
        }
        if (subCategories[i].subCategories.length > 0) {
          if (findAndInsert(subCategories[i].subCategories)) {
            return true;
          }
        }
      }
      return false;
    };

    if (draggedItem) {
      let inserted = false;

      if (position === ReorderPositions.InsideCategory) {
        const targetCategory = newData.find((c) => c.id === targetId);
        if (targetCategory) {
          targetCategory.subCategories.unshift(draggedItem);
          inserted = true;

          // Update the moved goal's lifeAreaId and parentIds
          if (onChangeGoal) {
            onChangeGoal((draggedItem as SubCategory).goal.id as ID, {
              lifeAreaId: targetCategory.id,
              parentIds: null,
            });
          }

          // Update the life area's goal sorting
          if (onChangeLifeArea) {
            onChangeLifeArea(targetCategory.id as ID, {
              goalSorting: targetCategory.subCategories.map((sc) => sc.goal.id),
            });
          }
        }
      } else {
        newData.forEach((c) => {
          if (findAndInsert(c.subCategories)) {
            inserted = true;

            // Find the parent goal that contains the reordered goals
            const findTargetGoal = (
              subCategories: SubCategory[],
              targetId: string,
            ): SubCategory | null => {
              for (const sc of subCategories) {
                if (sc.goal.id === targetId) {
                  return sc;
                }

                if (sc.subCategories.length) {
                  const found = findTargetGoal(sc.subCategories, targetId);
                  if (found) {
                    return found;
                  }
                }
              }
              return null;
            };

            const targetGoal = findTargetGoal(c.subCategories, targetId);

            if (targetGoal) {
              // Update the moved goal's lifeAreaId and parentIds
              if (onChangeGoal) {
                let parentGoal: SubCategory | null = null;
                // moving inside a goal means it becomes the parentGoal
                if (position === ReorderPositions.Inside) {
                  parentGoal = targetGoal;
                }

                // moving before or after a goal means we need to get the parent of that goal to have the parentGoal
                if (
                  [ReorderPositions.Before, ReorderPositions.After].includes(
                    position,
                  )
                ) {
                  parentGoal = targetGoal.goal.parentIds?.length
                    ? findTargetGoal(
                        c.subCategories,
                        targetGoal.goal.parentIds[
                          targetGoal.goal.parentIds.length - 1
                        ],
                      )
                    : null;
                }

                // update dragged goal
                onChangeGoal(draggedItem!.goal.id as ID, {
                  lifeAreaId: parentGoal ? null : c.id,
                  parentIds: parentGoal
                    ? [...(parentGoal.goal.parentIds ?? []), parentGoal.goal.id]
                    : null,
                });

                // update parent goal, if exists
                if (parentGoal) {
                  onChangeGoal(parentGoal.goal.id as ID, {
                    goalSorting: parentGoal.subCategories.map(
                      (sc) => sc.goal.id,
                    ),
                  });
                }
              }

              // If the target goal is at the top level, also update the life area
              if (!!targetGoal.goal.lifeAreaId && onChangeLifeArea) {
                onChangeLifeArea(c.id as ID, {
                  goalSorting: c.subCategories.map((sc) => sc.goal.id),
                });
              }
            }
          }
        });
      }
      if (inserted) {
        return newData;
      }
    }
  };

  return (
    <GoalTimeline
      ref={timelineRef}
      categories={categories}
      goals={goals}
      lifeAreas={lifeAreas}
      dateRange={dateRange}
      onUpdateDates={handleUpdateDates}
      onReorderGoals={handleReorderGoals}
      collapsedItems={collapsedItems}
      toggleCollapse={_toggleCollapse}
      onCategoryAdd={onCategoryAdd}
      timeframe={timeframe}
      referenceDate={referenceDate}
      weekStartsOn={weekStartsOn}
      title={title}
    />
  );
};
